import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService, CryptoService, LocalStorageService } from 'src/app/core/core.module';
import { FundsPortalService } from 'src/app/core/services/funds-portal.service';
import { AtramModalComponent } from 'src/app/shared/components/atram-modal/atram-modal.component';

@Component({
  selector: 'app-totp-verify',
  templateUrl: './totp-verify.component.html',
  styleUrls: ['./totp-verify.component.less']
})
export class TotpVerifyComponent implements OnInit {

  @ViewChild('atramModal')
  public atramModal: AtramModalComponent = new AtramModalComponent;

  public searchFormGroup!: FormGroup;
  public isError: boolean = false;
  public isInvalidCode: boolean = false;
  public isNotValidInputs: boolean = false;
  public useBackupCode: boolean = false;
  public invalidText: string = '';
  public isLockoutWarning: boolean = false;
  private failedAttemptCount: number = 0;

  private showLockoutWarningModal(): void {
    this.atramModal.show();
    this.atramModal.size = 'sm';
    this.atramModal.status = 'warning';
    this.atramModal.headerText = 'Warning: One Attempt Remaining';
    this.atramModal.bodyText = 'You have entered an incorrect code 4 times. One more incorrect attempt will lock your account.';
    this.atramModal.okButtonText = 'OK';
    this.atramModal.showCancelButton = false;
    this.atramModal.showCloseButton = false;
    this.atramModal.confirmationSubject.subscribe(() => {
      this.atramModal.hide();
    });
  }

  constructor(
    public fundsPortalService: FundsPortalService,
    public authService: AuthService,
    public router: Router,
    public localStorageService: LocalStorageService,
    public cryptoService: CryptoService
  ) {}

  ngOnInit(): void {
    this.initializeFormGroup();
  }

  initializeFormGroup(): void {
    this.searchFormGroup = new FormGroup({
      otp: new FormControl('', [Validators.required]),
      backupCode: new FormControl('')
    });
  }

  onOtpChange(e: any): void {
    this.searchFormGroup.get('otp')?.setValue(e);
    if (this.searchFormGroup.get('otp')?.value.length === 6) {
      this.isError = false;
      this.isInvalidCode = false;
    }
  }

  toggleBackupCode(): void {
    this.useBackupCode = !this.useBackupCode;
    this.isInvalidCode = false;
    this.isError = false;
    this.searchFormGroup.get('otp')?.setValue('');
    this.searchFormGroup.get('backupCode')?.setValue('');
  }

  proceed(): void {
    if (this.useBackupCode) {
      if (!this.searchFormGroup.get('backupCode')?.value?.trim()) {
        this.isError = true;
        this.isNotValidInputs = true;
        return;
      }
    } else {
      if (this.searchFormGroup.get('otp')?.value.length < 6) {
        this.isError = true;
        this.isNotValidInputs = true;
        return;
      }
    }
    this.isNotValidInputs = false;
    this.submit();
  }

  submit(): void {
    const rawCode = this.useBackupCode
      ? this.searchFormGroup.get('backupCode')?.value?.trim()
      : this.searchFormGroup.get('otp')?.value;

    const code = rawCode;
    const body = { code: code };

    this.fundsPortalService.postVerifyMfa(body).subscribe({
      next: (data: any) => {
        if (data['response_result'] === '0001') {
          localStorage.clear();
          this.localStorageService.set('authorized', '0001', data['response_data']['expires_in']);
          this.localStorageService.set('bearer', data['response_data']['access_token'], data['response_data']['expires_in']);
          this.localStorageService.set('companyCode', data['companyCode'], data['response_data']['expires_in']);
          this.router.navigateByUrl('/file-upload/mutual-funds', { skipLocationChange: false });
        } else {
          this.failedAttemptCount++;
          this.isInvalidCode = true;
          this.isLockoutWarning = this.failedAttemptCount >= 4;
          this.invalidText = 'Invalid code. Please try again.';
          if (this.failedAttemptCount === 4) this.showLockoutWarningModal();
        }
      },
      error: (error: any) => {
        const responseData = error['response_data'] ?? {};
        const isLocked = responseData['is_locked'] === true;

        if (isLocked) {
          this.isInvalidCode = false;
          this.atramModal.show();
          this.atramModal.size = 'sm';
          this.atramModal.status = 'error';
          this.atramModal.headerText = 'Account Locked';
          this.atramModal.bodyText = 'Your account was locked due to exceeded incorrect attempts. Please reset your password to unlock your account.';
          this.atramModal.okButtonText = 'Return to Sign In';
          this.atramModal.showCancelButton = false;
          this.atramModal.showCloseButton = false;
          this.atramModal.confirmationSubject.subscribe((confirmation: boolean) => {
            if (confirmation) {
              this.atramModal.hide();
              this.router.navigateByUrl('', { skipLocationChange: false });
            }
          });
        } else {
          this.failedAttemptCount++;
          this.isInvalidCode = true;
          this.isLockoutWarning = this.failedAttemptCount >= 4;
          this.invalidText = responseData['message'] ?? 'Invalid code. Please try again.';
          if (this.failedAttemptCount === 4) this.showLockoutWarningModal();
        }
      }
    });
  }

  goToSignIn(): void {
    this.router.navigateByUrl('', { skipLocationChange: false });
  }
}
